import axios from "axios";
import Cookies from "js-cookie";
import { QueryClient } from "react-query";

/*** --- APP SETTINGS --- ***/
export const APP_NAME = 'agl_portal';
export const BASE_API_URL = process.env.REACT_APP_BASE_API_URL
export const SECOND_API_URL = process.env.REACT_APP_SECOND_API_URL
export const LIBRARIAN_API_URL = process.env.REACT_APP_LIBRARIAN_API_URL
const TOKEN_KEY = process.env.REACT_APP_AUTH_COOKIE_NAME || "access_token";
const AUTH_SERVICE_URL = process.env.REACT_APP_AUTH_SERVICE_URL;
const APP_URL = process.env.REACT_APP_APP_URL;

const apiUrls = [BASE_API_URL, SECOND_API_URL, LIBRARIAN_API_URL];
export const apiClients = apiUrls.map((baseUrl) => axios.create({ baseURL: baseUrl }));

axios.defaults.baseURL = BASE_API_URL;

/** Helper: get the current access token from cookies or localStorage */
function getAccessToken(): string | null {
  return Cookies.get(TOKEN_KEY) ?? localStorage.getItem(TOKEN_KEY) ?? null;
}

/** Helper: clear auth state and redirect to login */
let isLoggingOut = false;
function handleAuthFailure() {
  if (isLoggingOut) return; // prevent multiple redirects
  isLoggingOut = true;

  Cookies.remove(TOKEN_KEY);
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
  localStorage.removeItem("user");

  window.alert("Session expired. Please login again.");

  const redirectUrl = APP_URL || window.location.origin;
  if (AUTH_SERVICE_URL) {
    window.location.href = `${AUTH_SERVICE_URL}?redirect_url=${encodeURIComponent(redirectUrl)}`;
  } else {
    window.location.href = redirectUrl;
  }
}

/** Librarian client: attach sessionId + Authorization header */
apiClients[2].interceptors.request.use((config) => {
  const user = localStorage.getItem('user');
  if (user) {
    if (!config.headers) {
      config.headers = {};
    }
    const parsedUser = JSON.parse(user);
    config.headers['sessionId'] = parsedUser.sessionID;
  }
  const token = getAccessToken();
  if (token) {
    if (!config.headers) {
      config.headers = {};
    }
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

/** All clients: intercept 401/403 responses to force logout */
apiClients.forEach((client) => {
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        handleAuthFailure();
      }
      return Promise.reject(error);
    }
  );
});

/*** --- PERMISSIONS SETTINGS --- ***/
export enum ROLES {
  PUBLIC = "Public",
  CUSTOMER = "Customer",
  SHIPPER = "Shipper"
}

/*** --- UI SETTINGS --- ***/
export const TOAST_DURATION = 10;
export const ERROR_TOAST_DURATION = 20;
export const APPLICATION_DISPLAY_NAME = 'AGL Portal'

/*** --- LOCAL STORAGE SETTINGS --- ***/
export function getLocalStorageKey(key: string) {
  return `${APP_NAME}_${key}`;
}

/*** --- REACT QUERY SETTINGS --- ***/
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    }
  }
})
